import React from 'react';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import { useContent } from '../../../contexts/ContentContext';
import { useTranslations } from '../../../contexts/TranslationContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useEditMode } from '../../../contexts/EditModeContext';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit';
import { formatCurrencyValue } from '../../../utils/formatCurrency';
import { useWinnersPage } from './WinnersPageContext';

export default function WinnersStatsBarSection() {
  const { c } = useContent();
  const { t } = useTranslations();
  const { language } = useLanguage();
  const { editMode, handleTranslationUpdate } = useEditMode();
  const { stats, gameType, winners } = useWinnersPage();

  if (!winners || winners.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
        <div className="flex items-center gap-3 mb-2">
          <Award className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
            <InlineTranslationEdit
              translationKey="WinnersStatsTotalWinners"
              value={t('winners.stats.totalWinners', 'Total Winners')}
              fallback="Total Winners"
              onUpdate={handleTranslationUpdate}
              editMode={editMode}
              className="text-sm font-medium"
            />
          </p>
        </div>
        <p className="text-3xl font-bold text-gray-900 dark:text-white">{stats.totalWinners}</p>
      </div>

      {gameType === 'prizeDraw' ? (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsPrizeTypes"
                  value={t('winners.stats.prizeTypes', 'Prize Types')}
                  fallback="Prize Types"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-lg font-bold text-green-600 dark:text-green-400">{stats.totalPrizes}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsFeaturedPrize"
                  value={t('winners.stats.featuredPrize', 'Featured Prize')}
                  fallback="Featured Prize"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-lg font-bold text-orange-600 dark:text-orange-400">{stats.featuredPrize}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Calendar className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsTotalDraws"
                  value={t('winners.stats.totalDraws', 'Total Draws')}
                  fallback="Total Draws"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.totalDraws}</p>
          </div>
        </>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsTotalPaidOut"
                  value={t('winners.stats.totalPaidOut', 'Total Paid Out')}
                  fallback="Total Paid Out"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-3xl font-bold text-green-600 dark:text-green-400">{formatCurrencyValue(stats.totalPaidOut, { language })}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsAverageWin"
                  value={t('winners.stats.averageWin', 'Average Win')}
                  fallback="Average Win"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-3xl font-bold text-orange-600 dark:text-orange-400">{formatCurrencyValue(stats.averageWin, { language })}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all hover:-translate-y-1">
            <div className="flex items-center gap-3 mb-2">
              <Trophy className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                <InlineTranslationEdit
                  translationKey="WinnersStatsBiggestWin"
                  value={t('winners.stats.biggestWin', 'Biggest Win')}
                  fallback="Biggest Win"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                  className="text-sm font-medium"
                />
              </p>
            </div>
            <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">{formatCurrencyValue(stats.biggestWin, { language })}</p>
          </div>
        </>
      )}
    </div>
  );
}
